
import { useCallback, useEffect, useState } from "react";
import { grnOfflineRepository } from "../repositories/grnRepository";
import { onNetworkStatusChange } from "../utils/network";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";
export const useGRNOffline = (authToken, userToken) => {
  const [grnList, setGRNList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : false
  );

  const refreshGRN = useCallback(async () => {
    if (!userToken) {
      setGRNList([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const data = await grnOfflineRepository.getGRNData(authToken, userToken);
      setGRNList(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [authToken, userToken]);

  const loadInvoices = useCallback(async () => {
    if (!userToken) {
      return [];
    }

    try {
      return await grnOfflineRepository.getInvoices(authToken, userToken);
    } catch (err) {
      setError(err);
      return [];
    }
  }, [authToken, userToken]);

  const createGRN = useCallback(
    async (grnPayload = []) => {
      if (!userToken) {
        return { success: false, offline: false };
      }

      setError(null);

      try {
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          await grnOfflineRepository.createOfflineGRN(grnPayload, userToken);
          const cached = await grnOfflineRepository.getGRNFromIndexedDB();
          setGRNList(cached);
          return { success: true, offline: true };
        }

        const beforeTokens = new Set(
          (Array.isArray(grnList) ? grnList : [])
            .map((row) => row?.grn_token)
            .filter(Boolean),
        );

        await grnOfflineRepository.createGRNOnline(
          authToken,
          grnPayload,
          userToken,
        );

        const fresh = await refreshGRN();

        try {
          const freshRows = Array.isArray(fresh) ? fresh : [];
          const newlyCreated = freshRows.filter(
            (row) => row?.grn_token && !beforeTokens.has(row.grn_token),
          );

          const getMostRecentGrnNumbers = (rows) => {
            const byNumber = new Map();
            rows.forEach((row) => {
              const num = row?.grn_number;
              if (!num) return;
              const t = new Date(row?.created_at || 0).getTime();
              const prev = byNumber.get(num);
              if (!prev || t > prev) {
                byNumber.set(num, Number.isNaN(t) ? 0 : t);
              }
            });
            const entries = Array.from(byNumber.entries());
            entries.sort((a, b) => b[1] - a[1]);
            const topTime = entries[0]?.[1];
            if (topTime == null) return [];
            return entries.filter((e) => e[1] === topTime).map((e) => e[0]);
          };

          const grnNumbers = Array.from(
            new Set([
              ...newlyCreated.map((r) => r?.grn_number).filter(Boolean),
              // Fallback: if diff fails (API doesn't change tokens), use the most recent GRN(s)
              ...getMostRecentGrnNumbers(freshRows),
            ]),
          );

          for (const grnNumber of grnNumbers) {
            await axios.post(
              API_ENDPOINT.create_asset_register_after_grn,
              { grn_number: grnNumber, org_token: userToken },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
          }
        } catch (assetErr) {
          // Non-blocking: GRN creation succeeded; asset creation can be retried later.
          console.error("Asset register creation after GRN failed:", assetErr);
        }

        return { success: true, offline: false };
      } catch (err) {
        setError(err);
        throw err;
      }
    },
    [authToken, grnList, refreshGRN, userToken]
  );

  const syncPendingGRN = useCallback(async () => {
    if (!authToken || !userToken) {
      return [];
    }

    try {
      const beforeTokens = new Set(
        (Array.isArray(grnList) ? grnList : [])
          .map((row) => row?.grn_token)
          .filter(Boolean),
      );

      const result = await grnOfflineRepository.syncOfflineGRN(
        authToken,
        userToken
      );

      if (result.length > 0) {
        const fresh = await refreshGRN();
        try {
          const freshRows = Array.isArray(fresh) ? fresh : [];
          const newlyCreated = freshRows.filter(
            (row) => row?.grn_token && !beforeTokens.has(row.grn_token),
          );

          const getMostRecentGrnNumbers = (rows) => {
            const byNumber = new Map();
            rows.forEach((row) => {
              const num = row?.grn_number;
              if (!num) return;
              const t = new Date(row?.created_at || 0).getTime();
              const prev = byNumber.get(num);
              if (!prev || t > prev) {
                byNumber.set(num, Number.isNaN(t) ? 0 : t);
              }
            });
            const entries = Array.from(byNumber.entries());
            entries.sort((a, b) => b[1] - a[1]);
            const topTime = entries[0]?.[1];
            if (topTime == null) return [];
            return entries.filter((e) => e[1] === topTime).map((e) => e[0]);
          };

          const grnNumbers = Array.from(
            new Set([
              ...newlyCreated.map((r) => r?.grn_number).filter(Boolean),
              ...getMostRecentGrnNumbers(freshRows),
            ]),
          );

          for (const grnNumber of grnNumbers) {
            await axios.post(
              API_ENDPOINT.create_asset_register_after_grn,
              { grn_number: grnNumber, org_token: userToken },
              { headers: { Authorization: `Bearer ${authToken}` } },
            );
          }
        } catch (assetErr) {
          console.error("Asset register creation after GRN failed:", assetErr);
        }
      }

      return result;
    } catch (err) {
      setError(err);
      return [];
    }
  }, [authToken, grnList, refreshGRN, userToken]);

  useEffect(() => {
    refreshGRN();
  }, [refreshGRN]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    const unsubscribe = onNetworkStatusChange(
      async () => {
        setIsOnline(true);
        await loadInvoices();
        await syncPendingGRN();
        await refreshGRN();
      },
      () => setIsOnline(false)
    );

    return unsubscribe;
  }, [loadInvoices, refreshGRN, syncPendingGRN]);

  return {
    grnList,
    loading,
    error,
    isOnline,
    createGRN,
    refreshGRN,
  };
};
